/**
 * LotSmith — Dev stub data provider.
 *
 * Provides a fake user + accounts + metrics so the UI can render end-to-end
 * before real telemetry exists. Supports a data-state simulator via URL
 * query so designers/QA can preview every state without backend changes:
 *
 *   ?state=loading   → isLoading=true on first render
 *   ?state=error     → error=... on first render
 *   ?state=empty     → empty accounts/trades/equity
 *   ?state=new-free  → FREE tier, one account, no trades yet (new user)
 *   ?state=pro-full  → PRO tier with 30-day history (default behavior)
 *
 * M12+ ships LiveProvider for real reads; this provider stays for
 * `?state=` previews and offline development.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { buildDemoData, type DemoTrade } from "../data/demo.js";
import { DataCtx, type DataState, type DevAccount, type DevCtx, type DevMetrics } from "./dataContext.js";

export type { DevAccount, DevMetrics, DevUser, DataState, EaStatus, DevCtx } from "./dataContext.js";
export { useDev } from "./dataContext.js";

const STUB_ACCOUNTS: DevAccount[] = [
  { id: "0", propFirm: "FTMO",       phase: "Phase 1", brokerLogin: "1234567", currency: "USD", startBalance: 100_000, currentEquity: 102_418.45,
    eaState: "NORMAL", eaStateReason: null, eaStateUpdatedAt: null },
  { id: "1", propFirm: "FundedNext", phase: "Phase 2", brokerLogin: "9988221", currency: "USD", startBalance:  50_000, currentEquity:  51_230.10,
    eaState: "NORMAL", eaStateReason: null, eaStateUpdatedAt: null },
];

const STUB_METRICS: DevMetrics = {
  equity: 102_418.45,
  equityDelta: 2_418.45,
  equityDeltaPct: 0.0242,
  dailyPnl: 348.12,
  tradesToday: 7,
  winRate: 0.71,
  dailyLossLeft: 4_651.88,
  dailyLossUsedPct: 0.46,
  totalLossLeft: 9_241.85,
  totalLossUsedPct: 0.08,
  highWaterEquity: 103_200.00,
  trailingDrawdown: 781.55,
  trailingDrawdownLeft: 9_218.45,
  trailingDrawdownUsedPct: 0.08,
  toTarget: 7_581.55,
  targetProgressPct: 0.34,
  minTradingDaysDone: 4,
  minTradingDaysRequired: 5,
  minTradingDaysProgressPct: 0.8,
  consistencyUsedPct: 0.32,
  consistencyLimitPct: 0.5,
  revengeTradesToday: 1,
  revengeWindowMinutes: 10,
  disciplineInsights: [
    {
      kind: "time_of_day",
      label: "Weak UTC hour",
      detail: "14:00 UTC · 5 trades · -$420",
      severity: "warning",
      value: -420,
    },
    {
      kind: "mistake_tag",
      label: "Repeated mistake",
      detail: "Moved SL · 3 tagged trades",
      severity: "warning",
      value: 3,
    },
  ],
  rules: {
    propFirm: "FTMO",
    dailyLossPct: 0.05,
    totalLossPct: 0.10,
    targetPct: 0.10,
    minTradingDays: 5,
    consistencyMaxDayPct: 0.5,
    resetTimezone: "Europe/Prague",
    newsPolicy: "allowed",
    newsBlackoutMinutes: 0,
    newsNotes: "Demo preview.",
  },
};

const NEW_FREE_ACCOUNT: DevAccount = {
  id: "0", propFirm: "FTMO", phase: "Phase 1", brokerLogin: "New",
  currency: "USD", startBalance: 100_000, currentEquity: 100_000,
  eaState: null, eaStateReason: null, eaStateUpdatedAt: null,
};

function readStateFromUrl(): DataState {
  if (typeof window === "undefined") return "pro-full";
  const q = new URLSearchParams(window.location.search).get("state");
  if (q === "loading" || q === "error" || q === "empty" || q === "new-free") return q;
  return "pro-full";
}

export function DevProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<DataState>(() => readStateFromUrl());
  const [isLoading, setIsLoading] = useState<boolean>(state === "loading");
  const [error, setError] = useState<string | null>(state === "error" ? "Couldn't load account data. Check your connection and try again." : null);
  const [activeAccountId, setActiveAccountId] = useState("0");

  const demo = useMemo(() => buildDemoData(), []);
  // Local journal overrides keep dev-mode edits visible across renders
  // without touching the deterministic demo dataset. Each tradeId maps
  // to a partial DemoTrade with whichever journal fields the user touched.
  const [journalOverrides, setJournalOverrides] = useState<Record<string, Partial<DemoTrade>>>({});
  const tradesWithNotes = useMemo<DemoTrade[]>(() => {
    if (Object.keys(journalOverrides).length === 0) return demo.trades;
    return demo.trades.map((t) => {
      const o = journalOverrides[t.id];
      return o ? { ...t, ...o } : t;
    });
  }, [demo.trades, journalOverrides]);

  const updateTradeNote = useCallback(async (tradeId: string, note: string | null) => {
    setJournalOverrides((prev) => ({
      ...prev,
      [tradeId]: { ...(prev[tradeId] ?? {}), note },
    }));
  }, []);

  const updateTradeJournal = useCallback(async (
    tradeId: string,
    patch: import("./dataContext.js").TradeJournalPatch,
  ) => {
    setJournalOverrides((prev) => ({
      ...prev,
      [tradeId]: { ...(prev[tradeId] ?? {}), ...patch },
    }));
  }, []);

  // Simulate a quick load when state=loading so the skeleton is visible.
  useEffect(() => {
    if (state !== "loading") return;
    const id = setTimeout(() => setIsLoading(false), 900);
    return () => clearTimeout(id);
  }, [state]);

  const value = useMemo<DevCtx>(() => {
    const reload = () => {
      setError(null);
      setIsLoading(true);
      setTimeout(() => {
        setIsLoading(false);
        setState("pro-full");
      }, 700);
    };

    // Empty state — no accounts, no trades.
    if (state === "empty") {
      return {
        isLoading: false, error: null,
        user: { email: "new@example.com", tier: "FREE" },
        accounts: [],
        activeAccountId: "",
        setActiveAccountId,
        activeAccount: undefined,
        metrics: null,
        equity: [],
        trades: [],
        eaStatus: "never-installed",
        lastSyncAt: null,
        reload,
        updateTradeNote,
        updateTradeJournal,
      };
    }

    // New-FREE — 1 account, FREE tier, no activity yet.
    if (state === "new-free") {
      return {
        isLoading: false, error: null,
        user: { email: "trader@example.com", tier: "FREE" },
        accounts: [NEW_FREE_ACCOUNT],
        activeAccountId: "0",
        setActiveAccountId,
        activeAccount: NEW_FREE_ACCOUNT,
        metrics: null,
        equity: [],
        trades: [],
        eaStatus: "never-installed",
        lastSyncAt: null,
        reload,
        updateTradeNote,
        updateTradeJournal,
      };
    }

    // Error state — no data, error banner.
    if (error) {
      return {
        isLoading: false, error,
        user: { email: "trader@example.com", tier: "PRO" },
        accounts: [],
        activeAccountId: "",
        setActiveAccountId,
        activeAccount: undefined,
        metrics: null,
        equity: [],
        trades: [],
        eaStatus: "offline",
        lastSyncAt: null,
        reload,
        updateTradeNote,
        updateTradeJournal,
      };
    }

    // Loading — keep accounts + trades empty, UI shows skeletons.
    if (isLoading) {
      return {
        isLoading: true, error: null,
        user: { email: "trader@example.com", tier: "PRO" },
        accounts: [],
        activeAccountId: "",
        setActiveAccountId,
        activeAccount: undefined,
        metrics: null,
        equity: [],
        trades: [],
        eaStatus: "idle",
        lastSyncAt: null,
        reload,
        updateTradeNote,
        updateTradeJournal,
      };
    }

    // Full — happy path with demo data.
    const active = STUB_ACCOUNTS.find((a) => a.id === activeAccountId) ?? STUB_ACCOUNTS[0];
    return {
      isLoading: false, error: null,
      user: { email: "trader@example.com", tier: "PRO" },
      accounts: STUB_ACCOUNTS,
      activeAccountId,
      setActiveAccountId,
      activeAccount: active,
      metrics: STUB_METRICS,
      equity: demo.equity,
      trades: tradesWithNotes,
      eaStatus: "connected",
      lastSyncAt: new Date(),
      reload,
      updateTradeNote,
        updateTradeJournal,
    };
  }, [state, isLoading, error, activeAccountId, demo, tradesWithNotes, updateTradeNote, updateTradeJournal]);

  return <DataCtx.Provider value={value}>{children}</DataCtx.Provider>;
}
